import React, { useState, useMemo } from 'react';
import { 
  ChevronLeft, X, Clock, Plus, Navigation, Calendar as CalendarIcon
} from 'lucide-react';
import { DndContext, DragOverlay, pointerWithin, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import JobBlock from './JobBlock';
import WeatherBadge, { WeatherWarning } from './WeatherBadge';
import ConflictModal from './ConflictModal';
import { formatCurrency } from '../../utils/helpers';

// ============================================
// HELPERS
// ============================================

// Time slots from 6 AM to 8 PM
const TIME_SLOTS = [];
for (let hour = 6; hour <= 20; hour++) {
  TIME_SLOTS.push(`${String(hour).padStart(2, '0')}:00`);
  if (hour < 20) {
    TIME_SLOTS.push(`${String(hour).padStart(2, '0')}:30`);
  }
}

// Format time for display (from HH:MM string)
const formatTimeDisplay = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Parse time from job's scheduledDate - returns HH:MM as stored in DB (LOCAL time)
const getJobTime = (job) => {
  if (!job.scheduledDate) return null;
  
  // Your system stores times as LOCAL, not UTC
  // Just extract the time portion directly from the ISO string
  const timePart = job.scheduledDate.split('T')[1];
  if (!timePart) return null;
  
  const [hours, minutes] = timePart.split(':');
  const h = parseInt(hours);
  const m = parseInt(minutes);
  
  // Check if midnight (no time set)
  if (h === 0 && m === 0) {
    return null;
  }
  
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// Round time to nearest slot
const roundToSlot = (timeStr) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const roundedMinutes = minutes < 15 ? 0 : minutes < 45 ? 30 : 0;
  const roundedHours = minutes >= 45 ? hours + 1 : hours;
  return `${String(roundedHours).padStart(2, '0')}:${String(roundedMinutes).padStart(2, '0')}`;
};

// Convert time string to minutes
const timeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

// ============================================
// DROPPABLE TIME SLOT
// ============================================

const DroppableTimeSlot = ({ time, children, hasJob }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `time-${time}`,
    data: { time, type: 'time-slot' },
  });

  const isHourMark = time.endsWith(':00');

  return (
    <div
      ref={setNodeRef}
      className={`
        relative min-h-[60px] border-b border-gray-100
        ${isHourMark ? 'border-gray-200' : ''}
        ${isOver ? 'bg-primary-50 ring-2 ring-inset ring-primary-300' : ''}
        ${!hasJob && !isOver ? 'hover:bg-gray-50' : ''}
        transition-colors
      `}
    >
      {/* Time label */}
      <div className={`
        absolute left-0 top-0 w-16 text-xs text-gray-400 py-1 px-2
        ${isHourMark ? 'font-medium text-gray-500' : ''}
      `}>
        {isHourMark ? formatTimeDisplay(time) : ''}
      </div>
      
      {/* Content area */}
      <div className="ml-16 pr-2 py-1">
        {children}
      </div>
    </div>
  );
};

// ============================================
// UNSCHEDULED ZONE
// ============================================

const UnscheduledZone = ({ children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: 'unscheduled-zone',
    data: { time: null, type: 'unscheduled' },
  });

  return (
    <div
      ref={setNodeRef}
      className={`
        min-h-[100px] border-2 border-dashed rounded-lg p-3
        ${isOver ? 'border-primary-400 bg-primary-50' : 'border-gray-200 bg-gray-50'}
        transition-colors
      `}
    >
      <div className="text-xs text-gray-400 mb-2 flex items-center gap-1">
        <Clock size={12} />
        No time set — drag here to unschedule time
      </div>
      <div className="space-y-2">
        {children}
      </div>
    </div>
  );
};

// ============================================
// DAY DETAIL VIEW
// ============================================

const DayDetailView = ({
  date,
  jobs,
  weather,
  onBack,
  onJobClick,
  onJobComplete,
  onJobEdit,
  onJobNavigate,
  onJobCall,
  onJobReschedule,
  onOptimizeRoute,
  onNewJob,
  isOwner = true,
  businessStartTime = '08:00',
  businessEndTime = '17:00',
}) => {
  const TIME_SLOTS = useMemo(() => {
    const slots = [];
    const [startHour] = businessStartTime.split(':').map(Number);
    const [endHour] = businessEndTime.split(':').map(Number);
    
    for (let hour = startHour; hour <= endHour; hour++) {
      slots.push(`${String(hour).padStart(2, '0')}:00`);
      if (hour < endHour) {
        slots.push(`${String(hour).padStart(2, '0')}:30`);
      }
    }
    return slots;
  }, [businessStartTime, businessEndTime]);

  // Configure drag sensor - require 8px movement before drag starts
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Drag state
  const [activeJob, setActiveJob] = useState(null);
  const [conflictModal, setConflictModal] = useState({
    isOpen: false,
    draggedJob: null,
    conflictingJobs: [],
    targetTime: null,
    nextAvailableTime: null,
  });

  // Reschedule confirmation state (for mobile drag-drop)
  const [rescheduleConfirm, setRescheduleConfirm] = useState({
    isOpen: false,
    job: null,
    fromTime: null,
    toTime: null,
    toUnscheduled: false,
  });

  // Format date for display
  const dateObj = new Date(date + 'T12:00:00');
  const isToday = date === (() => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
})();
  
  // Separate scheduled (with time) and unscheduled (no time) jobs
  const { scheduledJobs, unscheduledJobs } = useMemo(() => {
    const scheduled = [];
    const unscheduled = [];
    
    jobs.forEach(job => {
      const time = getJobTime(job);
      if (time) {
        scheduled.push({ ...job, _time: time, _slot: roundToSlot(time) });
      } else {
        unscheduled.push(job);
      }
    });
    
    return { scheduledJobs: scheduled, unscheduledJobs: unscheduled };
  }, [jobs]);

  // Group scheduled jobs by time slot
  const jobsBySlot = useMemo(() => {
    const grouped = {};
    TIME_SLOTS.forEach(slot => {
      grouped[slot] = [];
    });
    
    scheduledJobs.forEach(job => {
      const slot = job._slot;
      if (slot && grouped[slot]) {
        grouped[slot].push(job);
      } else if (slot) {
        // Job is at a time not in our slots (e.g., 7:15), put in nearest
        const nearest = roundToSlot(job._time);
        if (nearest && grouped[nearest]) {
          grouped[nearest].push(job);
        }
      }
    });
    
    return grouped;
  }, [scheduledJobs]);

  // Daily totals
  const dailyTotal = jobs.reduce((sum, j) => sum + (j.quoteAmount || 0), 0);

  // Drag handlers
  const handleDragStart = (event) => {
    const job = event.active.data.current?.job;
    if (job) {
      setActiveJob(job);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveJob(null);

    if (!over) {
      return;
    }

    const draggedJob = active.data.current?.job;
    const dropData = over.data.current;

    if (!draggedJob || !dropData) {
      return;
    }

    const currentTime = getJobTime(draggedJob);
    const targetTime = dropData.time;

    // If dropping in same slot, do nothing
    if (currentTime === targetTime) {
      return;
    }
    if (roundToSlot(currentTime) === targetTime) {
      return;
    }

    // If dropping in unscheduled zone - show confirmation
    if (dropData.type === 'unscheduled') {
      setRescheduleConfirm({
        isOpen: true,
        job: draggedJob,
        fromTime: currentTime,
        toTime: null,
        toUnscheduled: true,
      });
      return;
    }

    // Check for conflicts at target time
    const conflicts = checkConflicts(draggedJob, targetTime);

    if (conflicts.length > 0) {
      setConflictModal({
        isOpen: true,
        draggedJob,
        conflictingJobs: conflicts,
        targetTime,
        nextAvailableTime: findNextAvailableTime(targetTime, draggedJob),
      });
    } else {
      // Show confirmation instead of immediate reschedule
      setRescheduleConfirm({
        isOpen: true,
        job: draggedJob,
        fromTime: currentTime,
        toTime: targetTime,
        toUnscheduled: false,
      });
    }
  };
  
  // Handle reschedule confirmation
  const handleRescheduleConfirm = () => {
    const { job, toTime, toUnscheduled } = rescheduleConfirm;
    if (toUnscheduled) {
      onJobReschedule?.(job, date, null);
    } else {
      onJobReschedule?.(job, date, toTime);
    }
    setRescheduleConfirm({ isOpen: false, job: null, fromTime: null, toTime: null, toUnscheduled: false });
  };
  
  // Cancel reschedule
  const handleRescheduleCancel = () => {
    setRescheduleConfirm({ isOpen: false, job: null, fromTime: null, toTime: null, toUnscheduled: false });
  };

  // Check for time conflicts
  const checkConflicts = (draggedJob, targetTime) => {
    const draggedDuration = draggedJob.estimatedDurationMinutes || 60;
    const targetStart = timeToMinutes(targetTime);
    const targetEnd = targetStart + draggedDuration;

    return scheduledJobs.filter(job => {
      if (job.id === draggedJob.id) return false;
      
      const jobTime = job._time;
      if (!jobTime) return false;
      
      const jobDuration = job.estimatedDurationMinutes || 60;
      const jobStart = timeToMinutes(jobTime);
      const jobEnd = jobStart + jobDuration;

      return targetStart < jobEnd && targetEnd > jobStart;
    });
  };

  // Find next available time slot
  const findNextAvailableTime = (afterTime, draggedJob) => {
    const draggedDuration = draggedJob.estimatedDurationMinutes || 60;
    const afterMinutes = timeToMinutes(afterTime);
    
    // Get all busy periods
    const busyPeriods = scheduledJobs
      .filter(j => j.id !== draggedJob.id && j._time)
      .map(j => ({
        start: timeToMinutes(j._time),
        end: timeToMinutes(j._time) + (j.estimatedDurationMinutes || 60),
      }))
      .sort((a, b) => a.start - b.start);

    // Find first available slot after the requested time
    let candidateStart = afterMinutes + 30; // Start 30 min after
    const dayEnd = 20 * 60; // 8 PM

    for (const period of busyPeriods) {
      if (period.start > candidateStart) {
        // There's a gap before this period
        if (candidateStart + draggedDuration <= period.start) {
          break; // Found a slot
        }
      }
      if (period.end > candidateStart) {
        candidateStart = period.end;
      }
    }

    if (candidateStart + draggedDuration <= dayEnd) {
      const hours = Math.floor(candidateStart / 60);
      const minutes = candidateStart % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    return null;
  };

  // Conflict modal handlers
  const handleConflictConfirm = (choice, dontWarnAgain) => {
    if (dontWarnAgain) {
      localStorage.setItem('calendar_skip_conflict_warning', 'true');
    }

    if (choice === 'schedule-anyway') {
      onJobReschedule?.(
        conflictModal.draggedJob,
        date,
        conflictModal.targetTime
      );
    }

    setConflictModal({ ...conflictModal, isOpen: false });
  };

  const handleConflictAlternative = (newTime) => {
    onJobReschedule?.(conflictModal.draggedJob, date, newTime);
    setConflictModal({ ...conflictModal, isOpen: false });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col h-full bg-white">
        {/* Header */}
        <div className="sticky top-0 z-20 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <div>
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  {isToday ? 'Today' : dateObj.toLocaleDateString('en-US', { weekday: 'long' })}
                  {weather && <WeatherBadge weather={weather} compact />}
                </h2>
                <p className="text-sm text-gray-500">
                  {dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Summary */}
              <div className="text-sm text-gray-500 hidden sm:block">
                {jobs.length} jobs
                {isOwner && dailyTotal > 0 && (
                  <span className="ml-2 font-medium text-primary-600">
                    {formatCurrency(dailyTotal)}
                  </span>
                )}
              </div>

              {isOwner && onNewJob && (
                <button
                  onClick={() => onNewJob(date)}
                  className="btn btn-primary btn-sm flex items-center gap-1"
                >
                  <Plus size={16} />
                  <span className="hidden sm:inline">Add Job</span>
                </button>
              )}
            </div>
          </div>

          {/* Weather warning if rainy */}
          {weather?.isRainy && (
            <div className="px-4 pb-3">
              <WeatherWarning weather={weather} />
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto">
          {jobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-12 text-center">
              <CalendarIcon size={48} className="text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No jobs scheduled</p>
              <p className="text-sm text-gray-400 mt-1">
                Click "Add Job" to schedule work for this day
              </p>
            </div>
          ) : (
            <div className="pb-24">
              {/* Time slots */}
              {TIME_SLOTS.map((slot) => {
                const slotJobs = jobsBySlot[slot] || [];
                return (
                  <DroppableTimeSlot
                    key={slot}
                    time={slot}
                    hasJob={slotJobs.length > 0}
                  >
                    {slotJobs.map((job) => (
                      <JobBlock
                        key={job.id}
                        job={job}
                        compact
                        onClick={onJobClick}
                        onComplete={onJobComplete}
                        onEdit={onJobEdit}
                        onNavigate={onJobNavigate}
                        onCall={onJobCall}
                        isOwner={isOwner}
                      />
                    ))}
                  </DroppableTimeSlot>
                );
              })}

              {/* Unscheduled jobs zone */}
              {(unscheduledJobs.length > 0 || scheduledJobs.length > 0) && (
                <div className="p-4 border-t border-gray-200">
                  <UnscheduledZone>
                    {unscheduledJobs.map((job) => (
                      <JobBlock
                        key={job.id}
                        job={job}
                        compact
                        onClick={onJobClick}
                        onComplete={onJobComplete}
                        onEdit={onJobEdit}
                        onNavigate={onJobNavigate}
                        onCall={onJobCall}
                        isOwner={isOwner}
                      />
                    ))}
                    {unscheduledJobs.length === 0 && (
                      <p className="text-xs text-gray-400 text-center py-2">
                        Drag jobs here to remove their scheduled time
                      </p>
                    )}
                  </UnscheduledZone>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sticky bottom - Optimize & Navigate Buttons */}
        {jobs.length > 0 && (
          <div className="sticky bottom-0 p-4 bg-gradient-to-t from-white via-white to-transparent">
            {/* Show both buttons if 2+ scheduled jobs, otherwise just Navigate */}
            {jobs.filter(j => j.status === 'Scheduled' || j.status === 'In Progress').length >= 2 ? (
              <div className="flex gap-3">
                <button
                  onClick={() => onOptimizeRoute?.(date)}
                  className="flex-1 bg-white border-2 border-emerald-500 text-emerald-600 font-semibold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 hover:bg-emerald-50 active:scale-[0.98] transition-all"
                >
                  <Navigation size={18} />
                  Optimize
                </button>
                <button
                  onClick={() => {
                    // Navigate using scheduled order
                    const scheduledJobs = jobs
                      .filter(j => j.status === 'Scheduled' || j.status === 'In Progress')
                      .sort((a, b) => {
                        const timeA = getJobTime(a) || '99:99';
                        const timeB = getJobTime(b) || '99:99';
                        return timeA.localeCompare(timeB);
                      });

                    if (scheduledJobs.length === 1) {
                      const job = scheduledJobs[0];
                      const address = encodeURIComponent(`${job.address}, ${job.city}, ${job.state} ${job.zipCode}`);
                      window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
                      return;
                    }

                    const addresses = scheduledJobs.map(job => {
                      const parts = [job.address, job.city, job.state, job.zipCode].filter(Boolean);
                      return encodeURIComponent(parts.join(', '));
                    });

                    const destination = addresses[addresses.length - 1];
                    const waypoints = addresses.slice(0, -1).join('|');
                    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}&waypoints=${waypoints}`;
                    
                    window.open(mapsUrl, '_blank');
                  }}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all"
                >
                  <Navigation size={18} />
                  Navigate
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  // Single job or no scheduled jobs - just navigate
                  const scheduledJobs = jobs.filter(j => j.status === 'Scheduled' || j.status === 'In Progress');
                  if (scheduledJobs.length === 0) {
                    alert('No scheduled jobs for this day');
                    return;
                  }
                  
                  const job = scheduledJobs[0];
                  const address = encodeURIComponent(`${job.address}, ${job.city}, ${job.state} ${job.zipCode}`);
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
                }}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all"
              >
                <Navigation size={18} />
                Navigate
              </button>
            )}
          </div>
        )}

        {/* Drag Overlay */}
        <DragOverlay>
          {activeJob && (
            <JobBlock
              job={activeJob}
              compact
              isDragging
              isOwner={isOwner}
            />
          )}
        </DragOverlay>
      </div>

      {/* Conflict Modal */}
      <ConflictModal
        isOpen={conflictModal.isOpen}
        onClose={() => setConflictModal({ ...conflictModal, isOpen: false })}
        onConfirm={handleConflictConfirm}
        onSelectAlternative={handleConflictAlternative}
        draggedJob={conflictModal.draggedJob}
        conflictingJobs={conflictModal.conflictingJobs}
        targetTime={conflictModal.targetTime}
        targetDate={date}
        nextAvailableTime={conflictModal.nextAvailableTime}
        isOwner={isOwner}
      />
      
      {/* Reschedule Confirmation Modal */}
      {rescheduleConfirm.isOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/40"
            onClick={handleRescheduleCancel}
          />
          
          {/* Bottom Sheet */}
          <div 
            className="relative bg-white w-full rounded-t-2xl shadow-xl max-h-[50vh] overflow-hidden"
            style={{
              animation: 'slideUp 0.2s ease-out'
            }}
          >
            <style>{`
              @keyframes slideUp {
                from { transform: translateY(100%); }
                to { transform: translateY(0); }
              }
            `}</style>
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            
            {/* Content */}
            <div className="px-5 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Reschedule Job?
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {rescheduleConfirm.job?.customer?.name || 'This job'}
              </p>
              
              {/* Time Change Display */}
              <div className="flex items-center justify-center gap-3 bg-gray-50 rounded-xl p-4 mb-5">
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">From</p>
                  <p className="font-semibold text-gray-700">
                    {rescheduleConfirm.fromTime 
                      ? formatTimeDisplay(rescheduleConfirm.fromTime)
                      : 'Unscheduled'
                    }
                  </p>
                </div>
                <div className="text-2xl text-gray-300">→</div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">To</p>
                  <p className={`font-semibold ${rescheduleConfirm.toUnscheduled ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {rescheduleConfirm.toUnscheduled
                      ? 'Unscheduled'
                      : formatTimeDisplay(rescheduleConfirm.toTime)
                    }
                  </p>
                </div>
              </div>
              
              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleRescheduleCancel}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRescheduleConfirm}
                  className="flex-1 py-3 px-4 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DndContext>
  );
};

export default DayDetailView;